// GraficiRaddrizzatrici.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import {
  AreaChart, Area,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';

const TABLE_NAME = 'rd_cco';

// ─── PALETTE — ben contrastata, visibile su sfondo chiaro e scuro ──────────────
const PALETTE = [
  '#6366f1', // indigo
  '#f59e0b', // amber
  '#10b981', // emerald
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
];

// Colore stabile per una categoria (linea, tipo, colata...) in base alla sua posizione nell'elenco completo
const catColor = (val, allVals) => {
  if (!val) return '#94a3b8';
  const idx = allVals.indexOf(val);
  return PALETTE[idx % PALETTE.length];
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const getMese = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const getAnno = (iso) => {
  if (!iso) return null;
  return new Date(iso).getFullYear();
};

const MESI_IT = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
const meseBreve = (mmyyyy) => {
  if (!mmyyyy) return '';
  const [mm, yyyy] = mmyyyy.split('/');
  return `${MESI_IT[parseInt(mm,10)-1]} '${yyyy.slice(2)}`;
};

const fmt = (n) => Number(n).toLocaleString('it-IT');

// Formatta data_intervento in italiano dd/mm/yyyy hh:mm
const fmtDataOra = (iso) => {
  if (!iso) return '---';
  try {
    return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

// Ritorna il record più recente (per data_intervento) di un sottoinsieme di righe
const getUltimoRecord = (subset) => {
  if (!subset || !subset.length) return null;
  const sorted = [...subset].sort((a, b) => new Date(b.data_intervento || 0) - new Date(a.data_intervento || 0));
  return sorted[0];
};

// ─── TOOLTIP GENERICO ────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const sorted = [...payload].sort((a,b) => (b.value||0)-(a.value||0));
  const total  = sorted.reduce((s,p) => s + (p.value||0), 0);
  // Cerca un record "ultimo intervento" agganciato al dato aggregato corrente
  const ultimo = payload.find(p => p.payload?._ultimo)?.payload?._ultimo;

  return (
    <div className="bg-card border border-border/60 rounded-2xl shadow-2xl px-4 py-3.5 min-w-[190px] pointer-events-none">
      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50 mb-2.5 pb-2 border-b border-border/20">
        {label}
      </p>
      {sorted.map(p => (
        <div key={p.dataKey ?? p.name} className="flex items-center justify-between gap-4 mb-1.5">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.color ?? p.fill }} />
            <span className="text-[10px] font-bold text-muted-foreground/70">{p.name}</span>
          </div>
          <span className="text-[12px] font-black text-foreground tabular-nums">{fmt(p.value)}</span>
        </div>
      ))}
      {sorted.length > 1 && (
        <div className="flex justify-between pt-2 mt-1 border-t border-border/20">
          <span className="text-[9px] font-black uppercase text-muted-foreground/40">Totale</span>
          <span className="text-[12px] font-black text-primary tabular-nums">{fmt(total)}</span>
        </div>
      )}
      {ultimo && (
        <div className="flex flex-col gap-1.5 pt-2.5 mt-2.5 border-t border-border/20">
          <span className="text-[8px] font-black uppercase tracking-widest text-primary/60 mb-0.5">Ultimo Intervento</span>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">Colata</span>
            <span className="text-[10px] font-black text-foreground/80">{ultimo.colata || '---'}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">Tipo</span>
            <span className="text-[10px] font-black text-foreground/80">{ultimo.tipointervento || '---'}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">Linea</span>
            <span className="text-[10px] font-black text-foreground/80">{ultimo.linea || '---'}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">Posizione</span>
            <span className="text-[10px] font-black text-foreground/80">{ultimo.posizione || '---'}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">Data</span>
            <span className="text-[10px] font-mono font-black text-primary">{fmtDataOra(ultimo.data_intervento)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── TOOLTIP DEDICATO "Posizione per Linea" — mostra SOLO l'ultimo intervento ──
const PosizioneTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s,p) => s + (p.value||0), 0);
  const ultimo = payload.find(p => p.payload?._ultimo)?.payload?._ultimo;

  return (
    <div className="bg-card border border-border/60 rounded-2xl shadow-2xl px-4 py-3.5 min-w-[195px] pointer-events-none">
      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50 mb-2.5 pb-2 border-b border-border/20">
        Posizione {label}
      </p>
      <div className="flex items-center justify-between gap-4 mb-1">
        <span className="text-[10px] font-bold text-muted-foreground/60">Interventi totali</span>
        <span className="text-[13px] font-black text-primary tabular-nums">{fmt(total)}</span>
      </div>
      {ultimo ? (
        <div className="flex flex-col gap-1.5 pt-2.5 mt-2 border-t border-border/20">
          <span className="text-[8px] font-black uppercase tracking-widest text-primary/60 mb-0.5">Ultimo Intervento</span>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">Colata</span>
            <span className="text-[10px] font-black text-foreground/80">{ultimo.colata || '---'}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">Tipo</span>
            <span className="text-[10px] font-black text-foreground/80">{ultimo.tipointervento || '---'}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">Linea</span>
            <span className="text-[10px] font-black text-foreground/80">{ultimo.linea || '---'}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">Posizione</span>
            <span className="text-[10px] font-black text-foreground/80">{ultimo.posizione || '---'}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-wider">Data</span>
            <span className="text-[10px] font-mono font-black text-primary">{fmtDataOra(ultimo.data_intervento)}</span>
          </div>
        </div>
      ) : (
        <p className="text-[9px] font-bold text-muted-foreground/30 uppercase tracking-widest pt-2 mt-2 border-t border-border/20">Nessun intervento registrato</p>
      )}
    </div>
  );
};

// ─── KPI CARD ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, icon, color = '#6366f1', trend }) => (
  <div className="relative flex flex-col gap-3 p-5 rounded-3xl bg-card border border-border/40 overflow-hidden group hover:border-border/60 transition-colors">
    <div className="absolute inset-0 rounded-3xl transition-opacity opacity-0 group-hover:opacity-100"
      style={{ background: `radial-gradient(circle at 80% 20%, ${color}10, transparent 60%)` }} />
    <div className="flex items-center justify-between relative z-10">
      <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">{label}</span>
      <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
        <Icon name={icon} size={14} style={{ color }} />
      </div>
    </div>
    <div className="flex items-end justify-between relative z-10 gap-2">
      <span className="text-3xl font-black tracking-tighter leading-none" style={{ color }}>{value ?? '—'}</span>
      {trend != null && (
        <span className={`text-[10px] font-black flex items-center gap-0.5 mb-0.5 ${trend >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
          <Icon name={trend >= 0 ? 'TrendingUp' : 'TrendingDown'} size={11} />
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    {sub && <span className="text-[9px] font-bold text-muted-foreground/30 uppercase tracking-widest relative z-10 leading-tight">{sub}</span>}
  </div>
);

// ─── CHART CARD ──────────────────────────────────────────────────────────────
const ChartCard = ({ title, subtitle, contextLabel, icon, children, actions }) => (
  <div className="flex flex-col gap-5 p-6 rounded-3xl bg-muted/[0.06] border border-border/30 hover:border-border/50 transition-colors">
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="flex items-start gap-3">
        {icon && (
          <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center shrink-0 mt-0.5">
            <Icon name={icon} size={13} className="text-primary/70" />
          </div>
        )}
        <div className="flex flex-col gap-0.5">
          <p className="text-[12px] font-black uppercase tracking-widest text-foreground/80">{title}</p>
          {subtitle && <p className="text-[9px] font-bold text-muted-foreground/40 uppercase tracking-wider">{subtitle}</p>}
          {contextLabel && (
            <div className="flex items-center gap-1.5 mt-1">
              <div className="w-1 h-1 rounded-full bg-primary/50" />
              <span className="text-[9px] font-black text-primary/60 uppercase tracking-widest">{contextLabel}</span>
            </div>
          )}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
    {children}
  </div>
);

// ─── TOGGLE ──────────────────────────────────────────────────────────────────
const ChartToggle = ({ value, onChange, options }) => (
  <div className="flex gap-0.5 bg-muted/50 border border-border/30 p-0.5 rounded-xl">
    {options.map(o => (
      <button
        key={o.value}
        onClick={() => onChange(o.value)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all duration-150 ${
          value === o.value
            ? 'bg-card shadow text-foreground border border-border/40'
            : 'text-muted-foreground/40 hover:text-muted-foreground'
        }`}
      >
        <Icon name={o.icon} size={10} />
        {o.label}
      </button>
    ))}
  </div>
);

// ─── FILTER PILL (Colata, Linea) ────────────────────────────────────────────
const FilterGroup = ({ label, options, value, onChange, color }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/30 shrink-0 min-w-[32px]">{label}</span>
    <div className="flex gap-1.5 flex-wrap">
      {options.map(o => {
        const active = value === o;
        return (
          <button
            key={o}
            onClick={() => onChange(o)}
            className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-150 border ${
              active
                ? 'text-white border-transparent shadow-sm'
                : 'bg-muted/20 border-border/25 text-muted-foreground/50 hover:text-foreground hover:border-border/50'
            }`}
            style={active ? { background: color || '#6366f1' } : {}}
          >
            {o === 'TUTTO' ? 'Tutti' : o}
          </button>
        );
      })}
    </div>
  </div>
);

// ─── FILTER SELECT (dropdown singolo: Tipo, Posizione) ─────────────────────
const FilterSelect = ({ label, options, value, onChange, color = '#6366f1' }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/30 shrink-0 min-w-[32px]">{label}</span>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-muted/20 border border-border/25 rounded-xl pl-3 pr-7 py-1.5 text-[10px] font-black uppercase tracking-widest text-foreground/70 outline-none cursor-pointer transition-all hover:border-border/50 focus:border-primary/40"
        style={{ colorScheme: 'light dark' }}
      >
        {options.map(o => (
          <option key={o} value={o}>{o === 'TUTTO' ? 'Tutti' : o}</option>
        ))}
      </select>
      <Icon name="ChevronDown" size={10} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: `${color}99` }} />
    </div>
  </div>
);

// ─── FILTER MULTI-SELECT (dropdown con checkbox: Anno) ──────────────────────
const FilterMultiSelect = ({ label, options, selected, onChange, color = '#6366f1' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleOption = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter(v => v !== opt));
    else onChange([...selected, opt]);
  };

  const labelText = selected.length === 0
    ? 'Tutti'
    : selected.length === 1
      ? selected[0]
      : `${selected.length} selezionati`;

  return (
    <div className="flex items-center gap-2 flex-wrap" ref={ref}>
      <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/30 shrink-0 min-w-[32px]">{label}</span>
      <div className="relative">
        <button
          onClick={() => setOpen(o => !o)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all duration-150 ${
            selected.length > 0
              ? 'text-white border-transparent shadow-sm'
              : 'bg-muted/20 border-border/25 text-muted-foreground/50 hover:text-foreground hover:border-border/50'
          }`}
          style={selected.length > 0 ? { background: color } : {}}
        >
          {labelText}
          <Icon name="ChevronDown" size={10} className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute left-0 top-full mt-2 min-w-[150px] bg-card border-2 border-border/50 rounded-2xl shadow-2xl z-30 overflow-hidden py-1.5 max-h-64 overflow-y-auto animate-in fade-in zoom-in-95 duration-150">
            <button
              onClick={() => onChange([])}
              className={`w-full flex items-center justify-between gap-3 px-3.5 py-2 text-[10px] font-black uppercase tracking-widest transition-colors ${
                selected.length === 0 ? 'text-primary bg-primary/5' : 'text-muted-foreground/60 hover:text-foreground hover:bg-muted/30'
              }`}
            >
              Tutti gli anni
              {selected.length === 0 && <Icon name="Check" size={12} />}
            </button>
            <div className="h-px bg-border/20 my-1 mx-2" />
            {options.map(opt => {
              const active = selected.includes(opt);
              return (
                <button
                  key={opt}
                  onClick={() => toggleOption(opt)}
                  className={`w-full flex items-center justify-between gap-3 px-3.5 py-2 text-[10px] font-black uppercase tracking-widest transition-colors ${
                    active ? 'text-foreground bg-muted/40' : 'text-muted-foreground/60 hover:text-foreground hover:bg-muted/20'
                  }`}
                >
                  {opt}
                  <div
                    className="w-3.5 h-3.5 rounded-md border flex items-center justify-center shrink-0"
                    style={active ? { background: color, borderColor: color } : { borderColor: 'rgba(128,128,128,0.35)' }}
                  >
                    {active && <Icon name="Check" size={9} className="text-white" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── LEGENDA PILLS (generica: linee, tipi, colate...) ──────────────────────────
const LegendaPills = ({ categorie, allCategorie, dati }) => (
  <div className="flex flex-wrap gap-2 pt-1 border-t border-border/10">
    {categorie.map(c => {
      const tot = dati ? dati.reduce((s,d) => s + (d[c]||0), 0) : null;
      const col = catColor(c, allCategorie);
      return (
        <div key={c} className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl border"
          style={{ background: `${col}12`, borderColor: `${col}30` }}>
          <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: col }} />
          <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: col }}>
             {c}{tot !== null ? ` · ${tot}` : ''}
          </span>
        </div>
      );
    })}
  </div>
);

// ─── COMPONENTE PRINCIPALE ────────────────────────────────────────────────────
const GraficiRaddrizzatrici = ({ isOpen, onClose }) => {
  const [rows, setRows]           = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // annoSel ora è un ARRAY (multi-select) — [] = "Tutti gli anni"
  const [annoSel, setAnnoSel]         = useState([]);
  const [colataSel, setColataSel]     = useState('TUTTO');
  const [lineaSel, setLineaSel]       = useState('TUTTO');
  const [tipoSel, setTipoSel]         = useState('TUTTO');
  const [posizioneSel, setPosizioneSel] = useState('TUTTO');

  const [anni, setAnni]           = useState([]);
  const [colate, setColate]       = useState([]);
  const [lineeAll, setLineeAll]   = useState([]);
  const [tipiAll, setTipiAll]     = useState([]);
  const [posizioniAll, setPosizioniAll] = useState([]);

  const [tipoTrend, setTipoTrend]   = useState('area'); // area | bar | line
  const [tipoDistrib, setTipoDistrib] = useState('bar'); // bar | pie
  const [tipoAnno, setTipoAnno]     = useState('bar');   // bar | line

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('n_macchina, linea, colata, posizione, data_intervento, tipointervento, causa_freno, stato_rid')
        .not('data_intervento', 'is', null)
        .not('linea', 'is', null)
        .order('data_intervento', { ascending: true });
      if (error) throw error;

      const d = data ?? [];
      setRows(d);
      setAnni([...new Set(d.map(r => getAnno(r.data_intervento)).filter(Boolean))].sort((a,b) => b-a));
      setColate([...new Set(d.map(r => r.colata).filter(Boolean))].sort());
      setLineeAll([...new Set(d.map(r => r.linea).filter(Boolean))].sort((a,b) => a.localeCompare(b,undefined,{numeric:true})));
      setTipiAll([...new Set(d.map(r => r.tipointervento).filter(Boolean))].sort());
      setPosizioniAll([...new Set(d.map(r => r.posizione).filter(Boolean))].sort((a,b) => a.localeCompare(b,undefined,{numeric:true})));
    } catch (err) {
      console.error('GraficiRaddrizzatrici fetch:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { if (isOpen) fetchData(); }, [isOpen, fetchData]);

  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  // ── Reset filtri (helper condiviso) ───────────────────────────────────────
  const resetFiltri = () => {
    setAnnoSel([]); setColataSel('TUTTO'); setLineaSel('TUTTO'); setTipoSel('TUTTO'); setPosizioneSel('TUTTO');
  };

  // ── Filtraggio ────────────────────────────────────────────────────────────
  const rowsFiltrate = rows.filter(r => {
    if (annoSel.length > 0 && !annoSel.includes(String(getAnno(r.data_intervento)))) return false;
    if (colataSel    !== 'TUTTO' && r.colata !== colataSel) return false;
    if (lineaSel     !== 'TUTTO' && r.linea  !== lineaSel) return false;
    if (tipoSel      !== 'TUTTO' && (r.tipointervento || 'N/D') !== tipoSel) return false;
    if (posizioneSel !== 'TUTTO' && (r.posizione || 'N/D') !== posizioneSel) return false;
    return true;
  });

  const lineeVis = [...new Set(rowsFiltrate.map(r => r.linea).filter(Boolean))]
    .sort((a,b) => a.localeCompare(b,undefined,{numeric:true}));
  const tipiVis  = tipiAll.filter(t => rowsFiltrate.some(r => r.tipointervento === t));
  const posizioniVis = [...new Set(rowsFiltrate.map(r => r.posizione ?? 'N/D').filter(Boolean))]
    .sort((a,b) => a.localeCompare(b,undefined,{numeric:true}));

  // ── Label contesto filtri ─────────────────────────────────────────────────
  const contextLabel = [
    annoSel.length > 0 ? annoSel.join(', ') : null,
    colataSel    !== 'TUTTO' ? colataSel    : null,
    lineaSel     !== 'TUTTO' ? lineaSel     : null,
    tipoSel      !== 'TUTTO' ? tipoSel      : null,
    posizioneSel !== 'TUTTO' ? posizioneSel : null,
  ].filter(Boolean).join(' · ') || null;

  // ── DATI ─────────────────────────────────────────────────────────────────

  // 1. Trend mensile — unstacked, una serie per tipo intervento (+ _ultimo per tooltip)
  const datiTrend = (() => {
    const map = {};
    for (const r of rowsFiltrate) {
      const key = getMese(r.data_intervento);
      if (!key) continue;
      if (!map[key]) map[key] = { _key: key, _rows: [] };
      const t = r.tipointervento || 'N/D';
      map[key][t] = (map[key][t] ?? 0) + 1;
      map[key]['_tot'] = (map[key]['_tot'] ?? 0) + 1;
      map[key]._rows.push(r);
    }
    return Object.values(map)
      .sort((a,b) => {
        const [ma,ya] = a._key.split('/');
        const [mb,yb] = b._key.split('/');
        return new Date(ya, ma-1) - new Date(yb, mb-1);
      })
      .map(d => ({ ...d, mese: meseBreve(d._key), _ultimo: getUltimoRecord(d._rows) }));
  })();

  // 2. Distribuzione per linea (+ _ultimo)
  const datiLinea = lineeVis.map(l => {
    const matchRows = rowsFiltrate.filter(r => r.linea === l);
    return {
      linea: l,
      _raw: l,
      interventi: matchRows.length,
      _ultimo: getUltimoRecord(matchRows),
    };
  }).sort((a,b) => b.interventi - a.interventi);

  // 3. Distribuzione per tipo intervento (+ _ultimo)
  const datiTipo = tipiVis.map(t => {
    const matchRows = rowsFiltrate.filter(r => r.tipointervento === t);
    return {
      tipo: t,
      _raw: t,
      interventi: matchRows.length,
      _ultimo: getUltimoRecord(matchRows),
    };
  }).sort((a,b) => b.interventi - a.interventi);

  // 4. Top 10 macchine (+ _ultimo)
  const datiMacchine = (() => {
    const map = {};
    for (const r of rowsFiltrate) {
      const m = r.n_macchina ?? 'N/D';
      if (!map[m]) map[m] = { count: 0, rows: [] };
      map[m].count += 1;
      map[m].rows.push(r);
    }
    return Object.entries(map)
      .map(([m, v]) => ({ macchina: m, interventi: v.count, _ultimo: getUltimoRecord(v.rows) }))
      .sort((a,b) => b.interventi - a.interventi)
      .slice(0,10);
  })();

  // 5. Tipo Intervento per Linea — grouped bar (x = linea, serie = tipo) (+ _ultimo per linea)
  const datiTipoPerLinea = lineeVis.map(l => {
    const matchRows = rowsFiltrate.filter(r => r.linea === l);
    const obj = { linea: l, _ultimo: getUltimoRecord(matchRows) };
    tipiAll.forEach(t => {
      obj[t] = matchRows.filter(r => r.tipointervento === t).length;
    });
    return obj;
  });

  // 6. Tipo Intervento per Colata — grouped bar (x = colata, serie = tipo) (+ _ultimo per colata)
  const colateVis = colate.filter(c => rowsFiltrate.some(r => r.colata === c));
  const datiTipoPerColata = colateVis.map(c => {
    const matchRows = rowsFiltrate.filter(r => r.colata === c);
    const obj = { colata: c, _ultimo: getUltimoRecord(matchRows) };
    tipiAll.forEach(t => {
      obj[t] = matchRows.filter(r => r.tipointervento === t).length;
    });
    return obj;
  });

  // 7. Distribuzione per posizione — totale singolo (+ _ultimo)
  const datiPosizione = (() => {
    const map = {};
    for (const r of rowsFiltrate) {
      const p = r.posizione ?? 'N/D';
      if (!map[p]) map[p] = { count: 0, rows: [] };
      map[p].count += 1;
      map[p].rows.push(r);
    }
    return Object.entries(map)
      .map(([p, v]) => ({ posizione: p, interventi: v.count, _ultimo: getUltimoRecord(v.rows) }))
      .sort((a,b) => b.interventi - a.interventi);
  })();

  // 7b. NUOVO — Posizione per Linea: grouped bar (x = posizione, serie = linea) (+ _ultimo per posizione)
  const datiPosizionePerLinea = posizioniVis.map(p => {
    const matchRows = rowsFiltrate.filter(r => (r.posizione ?? 'N/D') === p);
    const obj = { posizione: p, _ultimo: getUltimoRecord(matchRows) };
    lineeVis.forEach(l => {
      obj[l] = matchRows.filter(r => r.linea === l).length;
    });
    return obj;
  });

  // 8. Confronto tra anni per linea — grouped bar (supporta selezione multipla) (+ _ultimo per anno)
  const datiAnno = (() => {
    const anniT = annoSel.length > 0 ? annoSel.map(Number) : anni;
    return anniT.map(a => {
      const obj = { anno: String(a) };
      const matchRowsAll = [];
      for (const l of lineeAll) {
        const matchRows = rows.filter(r =>
          getAnno(r.data_intervento) === Number(a) &&
          r.linea === l &&
          (colataSel    === 'TUTTO' || r.colata === colataSel) &&
          (tipoSel      === 'TUTTO' || r.tipointervento === tipoSel) &&
          (posizioneSel === 'TUTTO' || (r.posizione || 'N/D') === posizioneSel)
        );
        obj[l] = matchRows.length;
        matchRowsAll.push(...matchRows);
      }
      obj._ultimo = getUltimoRecord(matchRowsAll);
      return obj;
    }).sort((a,b) => Number(a.anno) - Number(b.anno));
  })();

  // 9. Causa Freno — SI / NO (sezione non modificata)
  const causaSi = rowsFiltrate.filter(r => r.causa_freno === true).length;
  const causaNo = rowsFiltrate.filter(r => r.causa_freno === false).length;
  const causaNoti = causaSi + causaNo;
  const causaPct = causaNoti > 0 ? Math.round((causaSi / causaNoti) * 100) : 0;
  const datiCausaFreno = [
    { name: 'SI', value: causaSi, color: '#ef4444' },
    { name: 'NO', value: causaNo, color: '#10b981' },
  ];

  // ── KPI ───────────────────────────────────────────────────────────────────
  const totale       = rowsFiltrate.length;
  const macchineUniche = new Set(rowsFiltrate.map(r => r.n_macchina).filter(Boolean)).size;
  const lineaTop      = datiLinea[0];
  const mediaLuna      = datiTrend.length ? (totale / datiTrend.length).toFixed(1) : '0';
  const trendYoy = (() => {
    if (anni.length < 2) return null;
    const c = rows.filter(r => getAnno(r.data_intervento) === anni[0]).length;
    const p = rows.filter(r => getAnno(r.data_intervento) === anni[1]).length;
    if (!p) return null;
    return Math.round(((c-p)/p)*100);
  })();

  const hasFiltri = annoSel.length>0||colataSel!=='TUTTO'||lineaSel!=='TUTTO'||tipoSel!=='TUTTO'||posizioneSel!=='TUTTO';

  // Stile assi comune
  const AXIS_STYLE = { fontSize:9, fontWeight:700, fill:'rgba(128,128,128,0.5)' };
  const GRID_PROPS = { strokeDasharray:'3 3', stroke:'rgba(128,128,128,0.07)', vertical:false };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/85 backdrop-blur-xl animate-in fade-in duration-300"
        onClick={onClose}
      />

      <div className="relative w-full max-w-6xl bg-card border border-border/50 shadow-2xl rounded-[2.5rem] overflow-hidden flex flex-col max-h-[94vh] animate-in zoom-in-95 duration-300">

        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

        {/* ── HEADER ── */}
        <div className="shrink-0 px-7 py-5 border-b border-border/30 bg-card/90 backdrop-blur-sm flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Icon name="BarChart2" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-black uppercase tracking-tighter text-foreground">
                Dashboard Interventi Raddrizzatrici
              </h2>
              <p className="text-[9px] font-black uppercase tracking-[.25em] text-muted-foreground/40 mt-0.5">
                Analisi interventi · Raddrizzatrici CCO
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasFiltri && (
              <button
                onClick={resetFiltri}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500/80 text-[10px] font-black uppercase tracking-widest hover:bg-red-500/15 transition-all"
              >
                <Icon name="FilterX" size={12} />
                Reset filtri
              </button>
            )}
            <button onClick={fetchData} disabled={isLoading}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-muted/40 border border-border/30 text-muted-foreground hover:text-primary transition-all disabled:opacity-40">
              <Icon name="RefreshCw" size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-2xl bg-muted/50 hover:bg-red-500/10 hover:text-red-500 border border-border/40 text-muted-foreground transition-all group">
              <Icon name="X" size={17} className="group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>
        </div>

        {/* ── FILTRI ── */}
        <div className="shrink-0 px-7 py-4 border-b border-border/15 bg-muted/5 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-x-7 gap-y-2.5">
            <FilterMultiSelect label="Anno"      options={anni.map(String)}          selected={annoSel}   onChange={setAnnoSel}      color="#6366f1" />
            <FilterGroup       label="Colata"    options={['TUTTO',...colate]}       value={colataSel}    onChange={setColataSel}    color="#f59e0b" />
            <FilterGroup       label="Linea"     options={['TUTTO',...lineeAll]}     value={lineaSel}     onChange={setLineaSel}     color="#8b5cf6" />
            <FilterSelect      label="Tipo"      options={['TUTTO',...tipiAll]}      value={tipoSel}      onChange={setTipoSel}      color="#06b6d4" />
            <FilterSelect      label="Posizione" options={['TUTTO',...posizioniAll]} value={posizioneSel} onChange={setPosizioneSel} color="#ec4899" />
          </div>
          {hasFiltri && (
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-pulse" />
              <span className="text-[9px] font-black uppercase tracking-widest text-primary/50">
                Filtro attivo: {contextLabel}
              </span>
            </div>
          )}
        </div>

        {/* ── CORPO ── */}
        <div
          className="flex-1 overflow-y-auto px-7 py-6 flex flex-col gap-6"
          style={{ scrollbarWidth:'thin', scrollbarColor:'rgba(128,128,128,0.15) transparent' }}
        >
          {isLoading ? (
            <div className="flex flex-col gap-4">
              {[260,210,210,250].map((h,i) => (
                <div key={i} className="rounded-3xl bg-muted/20 animate-pulse" style={{height:h}} />
              ))}
            </div>

          ) : rowsFiltrate.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-28 gap-4">
              <div className="w-16 h-16 rounded-full bg-muted/20 flex items-center justify-center">
                <Icon name="BarChart2" size={28} className="text-muted-foreground/15" />
              </div>
              <span className="text-sm font-black uppercase tracking-widest text-muted-foreground/25">
                Nessun dato per i filtri selezionati
              </span>
              {hasFiltri && (
                <button
                  onClick={resetFiltri}
                  className="text-[10px] font-black uppercase tracking-widest text-primary/60 hover:text-primary transition-colors"
                >
                  Azzera filtri
                </button>
              )}
            </div>

          ) : (
            <>
              {/* ── KPI ── */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard label="Tot. Interventi"    value={fmt(totale)}          sub={contextLabel ?? 'storico completo'}      icon="RefreshCw" color="#6366f1" trend={trendYoy} />
                <KpiCard label="Macchine Coinvolte" value={fmt(macchineUniche)}  sub="macchine distinte"                        icon="Settings"  color="#06b6d4" />
                <KpiCard label="Linea più Attiva"   value={lineaTop ? lineaTop.linea : '—'} sub={lineaTop ? `${fmt(lineaTop.interventi)} interventi` : ''} icon="Zap" color="#f59e0b" />
                <KpiCard label="Causa Freno"        value={causaNoti ? `${causaPct}%` : '—'} sub={causaNoti ? `${fmt(causaSi)} su ${fmt(causaNoti)} interventi noti` : 'nessun dato'} icon="AlertTriangle" color="#ef4444" />
              </div>

              {/* ── TREND MENSILE PER TIPO ── */}
              {datiTrend.length > 0 && (
                <ChartCard
                  title="Andamento mensile per Tipo Intervento"
                  subtitle={`${datiTrend.length} mesi — media ${mediaLuna}/mese`}
                  icon="TrendingUp"
                  contextLabel={contextLabel}
                  actions={
                    <ChartToggle value={tipoTrend} onChange={setTipoTrend} options={[
                      { value:'area', icon:'AreaChart',  label:'Area' },
                      { value:'bar',  icon:'BarChart2',  label:'Bar'  },
                      { value:'line', icon:'TrendingUp', label:'Line' },
                    ]} />
                  }
                >
                  <ResponsiveContainer width="100%" height={260}>
                    {tipoTrend === 'bar' ? (
                      <BarChart data={datiTrend} margin={{top:5,right:5,left:-18,bottom:0}} barGap={2} barCategoryGap="30%">
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="mese" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(128,128,128,0.05)' }} />
                        {tipiVis.map(t => (
                          <Bar key={t} dataKey={t} name={t}
                            fill={catColor(t,tipiAll)}
                            radius={[4,4,0,0]}
                            maxBarSize={28}
                          />
                        ))}
                      </BarChart>

                    ) : tipoTrend === 'line' ? (
                      <LineChart data={datiTrend} margin={{top:5,right:5,left:-18,bottom:0}}>
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="mese" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ stroke:'rgba(128,128,128,0.15)', strokeWidth:1 }} />
                        {tipiVis.map(t => (
                          <Line key={t} type="monotone" dataKey={t} name={t}
                            stroke={catColor(t,tipiAll)}
                            strokeWidth={2.5}
                            dot={{ r:3, fill:catColor(t,tipiAll), strokeWidth:0 }}
                            activeDot={{ r:5, strokeWidth:0 }}
                            connectNulls
                          />
                        ))}
                      </LineChart>

                    ) : (
                      <AreaChart data={datiTrend} margin={{top:5,right:5,left:-18,bottom:0}}>
                        <defs>
                          {tipiVis.map(t => {
                            const col = catColor(t,tipiAll);
                            return (
                              <linearGradient key={t} id={`ag-${t}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%"   stopColor={col} stopOpacity={0.22} />
                                <stop offset="100%" stopColor={col} stopOpacity={0.01} />
                              </linearGradient>
                            );
                          })}
                        </defs>
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="mese" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ stroke:'rgba(128,128,128,0.12)', strokeWidth:1 }} />
                        {tipiVis.map(t => (
                          <Area key={t} type="monotone" dataKey={t} name={t}
                            stroke={catColor(t,tipiAll)}
                            strokeWidth={2.5}
                            fill={`url(#ag-${t})`}
                            fillOpacity={1}
                            connectNulls
                            dot={false}
                            activeDot={{ r:4, strokeWidth:0, fill:catColor(t,tipiAll) }}
                          />
                        ))}
                      </AreaChart>
                    )}
                  </ResponsiveContainer>
                  <LegendaPills categorie={tipiVis} allCategorie={tipiAll} dati={datiTrend} />
                </ChartCard>
              )}

              {/* ── ROW: Distribuzione Tipo + Top Macchine ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {datiTipo.length > 0 && (
                  <ChartCard
                    title="Distribuzione per Tipo Intervento"
                    subtitle="Interventi totali per tipologia"
                    icon="Wrench"
                    contextLabel={contextLabel}
                    actions={
                      <ChartToggle value={tipoDistrib} onChange={setTipoDistrib} options={[
                        { value:'bar', icon:'BarChart2', label:'Bar' },
                        { value:'pie', icon:'PieChart',  label:'Pie' },
                      ]} />
                    }
                  >
                    {tipoDistrib === 'pie' ? (
                      <>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={datiTipo}
                              dataKey="interventi"
                              nameKey="tipo"
                              cx="50%" cy="50%"
                              innerRadius={50}
                              outerRadius={88}
                              paddingAngle={3}
                              strokeWidth={0}
                            >
                              {datiTipo.map((d,i) => (
                                <Cell key={i} fill={catColor(d._raw,tipiAll)} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="flex flex-col gap-1.5 pt-2 border-t border-border/10">
                          {datiTipo.map(d => {
                            const pct = totale > 0 ? ((d.interventi/totale)*100).toFixed(1) : 0;
                            const col = catColor(d._raw,tipiAll);
                            return (
                              <div key={d._raw} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full shrink-0" style={{background:col}} />
                                  <span className="text-[10px] font-bold text-foreground/70">{d.tipo}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="w-24 h-1.5 rounded-full bg-muted/30 overflow-hidden">
                                    <div className="h-full rounded-full" style={{width:`${pct}%`,background:col}} />
                                  </div>
                                  <span className="text-[10px] font-black tabular-nums" style={{color:col}}>{d.interventi}</span>
                                  <span className="text-[9px] text-muted-foreground/40 font-bold w-8 text-right">{pct}%</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={datiTipo} margin={{top:5,right:5,left:-18,bottom:0}} barCategoryGap="35%">
                          <CartesianGrid {...GRID_PROPS} />
                          <XAxis dataKey="tipo" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                          <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                          <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(128,128,128,0.05)' }} />
                          <Bar dataKey="interventi" name="Interventi" radius={[8,8,0,0]} maxBarSize={55}>
                            {datiTipo.map((d,i) => (
                              <Cell key={i} fill={catColor(d._raw,tipiAll)} />
                            ))}
                            <LabelList dataKey="interventi" position="top"
                              style={{ fontSize:10, fontWeight:900, fill:'rgba(128,128,128,0.6)' }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </ChartCard>
                )}

                {datiMacchine.length > 0 && (
                  <ChartCard
                    title="Top 10 macchine"
                    subtitle="più frequentemente interessate da interventi"
                    icon="Hash"
                    contextLabel={contextLabel}
                  >
                    <ResponsiveContainer width="100%" height={datiMacchine.length * 28 + 20} minHeight={200}>
                      <BarChart
                        data={datiMacchine}
                        layout="vertical"
                        margin={{top:0,right:45,left:0,bottom:0}}
                        barCategoryGap="25%"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.07)" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis
                          type="category"
                          dataKey="macchina"
                          width={90}
                          tick={{ fontSize:9, fontWeight:700, fill:'rgba(128,128,128,0.55)' }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(128,128,128,0.05)' }} />
                        <Bar dataKey="interventi" name="Interventi" radius={[0,7,7,0]} maxBarSize={16}>
                          {datiMacchine.map((_,i) => (
                            <Cell key={i} fill={`rgba(99,102,241,${0.95 - i*0.08})`} />
                          ))}
                          <LabelList dataKey="interventi" position="right"
                            style={{ fontSize:10, fontWeight:900, fill:'rgba(99,102,241,0.7)' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}
              </div>

              {/* ── TIPO INTERVENTO PER LINEA ── */}
              {datiTipoPerLinea.length > 0 && (
                <ChartCard
                  title="Tipo Intervento per Linea"
                  subtitle="quale tipologia di guasto colpisce ogni linea"
                  icon="GitBranch"
                  contextLabel={contextLabel}
                >
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={datiTipoPerLinea} margin={{top:5,right:5,left:-18,bottom:0}} barGap={2} barCategoryGap="30%">
                      <CartesianGrid {...GRID_PROPS} />
                      <XAxis dataKey="linea" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(128,128,128,0.05)' }} />
                      {tipiAll.map(t => (
                        <Bar key={t} dataKey={t} name={t}
                          fill={catColor(t,tipiAll)}
                          radius={[4,4,0,0]}
                          maxBarSize={24}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                  <LegendaPills categorie={tipiAll.filter(t => datiTipoPerLinea.some(d => d[t] > 0))} allCategorie={tipiAll} />
                </ChartCard>
              )}

              {/* ── ROW: Tipo per Colata + Posizione (totale) ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {datiTipoPerColata.length > 0 && (
                  <ChartCard
                    title="Tipo Intervento per Colata"
                    subtitle="distribuzione tipologie per colata"
                    icon="Layers"
                    contextLabel={contextLabel}
                  >
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={datiTipoPerColata} margin={{top:5,right:5,left:-18,bottom:0}} barGap={2} barCategoryGap="30%">
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="colata" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(128,128,128,0.05)' }} />
                        {tipiAll.map(t => (
                          <Bar key={t} dataKey={t} name={t}
                            fill={catColor(t,tipiAll)}
                            radius={[4,4,0,0]}
                            maxBarSize={24}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}

                {datiPosizione.length > 0 && (
                  <ChartCard
                    title="Distribuzione per Posizione"
                    subtitle="dove avvengono più interventi (totale)"
                    icon="MapPin"
                    contextLabel={contextLabel}
                  >
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={datiPosizione} layout="vertical" margin={{top:5,right:35,left:0,bottom:0}} barCategoryGap="30%">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.07)" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis
                          type="category"
                          dataKey="posizione"
                          width={120}
                          tick={{ fontSize:9, fontWeight:700, fill:'rgba(128,128,128,0.55)' }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(128,128,128,0.05)' }} />
                        <Bar dataKey="interventi" name="Interventi" fill="#8b5cf6" radius={[0,7,7,0]} maxBarSize={18}>
                          <LabelList dataKey="interventi" position="right"
                            style={{ fontSize:10, fontWeight:900, fill:'rgba(139,92,246,0.7)' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}
              </div>

              {/* ── NUOVO: POSIZIONE PER LINEA ── */}
              {datiPosizionePerLinea.length > 0 && (
                <ChartCard
                  title="Posizione per Linea"
                  subtitle={lineaSel !== 'TUTTO' ? `distribuzione posizioni — Linea ${lineaSel}` : 'confronto posizioni tra le linee selezionate'}
                  icon="MapPin"
                  contextLabel={contextLabel}
                >
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={datiPosizionePerLinea} margin={{top:5,right:5,left:-18,bottom:0}} barGap={2} barCategoryGap="30%">
                      <CartesianGrid {...GRID_PROPS} />
                      <XAxis dataKey="posizione" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                      <Tooltip content={<PosizioneTooltip />} cursor={{ fill:'rgba(128,128,128,0.05)' }} />
                      {lineeVis.map(l => (
                        <Bar key={l} dataKey={l} name={`Linea ${l}`}
                          fill={catColor(l,lineeAll)}
                          radius={[4,4,0,0]}
                          maxBarSize={28}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                  <LegendaPills categorie={lineeVis} allCategorie={lineeAll} dati={datiPosizionePerLinea} />
                </ChartCard>
              )}

              {/* ── CAUSA FRENO (sezione non modificata) ── */}
              {causaNoti > 0 && (
                <ChartCard
                  title="Causa Freno"
                  subtitle="quota di interventi causati da guasto al freno"
                  icon="AlertTriangle"
                  contextLabel={contextLabel}
                >
                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    <ResponsiveContainer width="100%" height={180} style={{ maxWidth: 220 }}>
                      <PieChart>
                        <Pie
                          data={datiCausaFreno}
                          dataKey="value"
                          nameKey="name"
                          cx="50%" cy="50%"
                          innerRadius={48}
                          outerRadius={78}
                          paddingAngle={3}
                          strokeWidth={0}
                        >
                          {datiCausaFreno.map((d,i) => <Cell key={i} fill={d.color} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-col gap-3 flex-1 w-full">
                      <div className="flex items-center justify-between px-4 py-3 rounded-2xl bg-red-500/10 border border-red-500/20">
                        <div className="flex items-center gap-2">
                          <Icon name="AlertTriangle" size={14} className="text-red-500" />
                          <span className="text-[11px] font-black uppercase tracking-widest text-red-500">Causa Freno</span>
                        </div>
                        <span className="text-lg font-black text-red-500 tabular-nums">{fmt(causaSi)}</span>
                      </div>
                      <div className="flex items-center justify-between px-4 py-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
                        <div className="flex items-center gap-2">
                          <Icon name="CheckCircle2" size={14} className="text-emerald-500" />
                          <span className="text-[11px] font-black uppercase tracking-widest text-emerald-600">Nessuna Causa Freno</span>
                        </div>
                        <span className="text-lg font-black text-emerald-600 tabular-nums">{fmt(causaNo)}</span>
                      </div>
                      <span className="text-[9px] font-bold text-muted-foreground/40 uppercase tracking-widest px-1">
                        {fmt(causaPct)}% degli interventi con dato noto sono legati a un guasto al freno
                      </span>
                    </div>
                  </div>
                </ChartCard>
              )}

              {/* ── CONFRONTO TRA ANNI PER LINEA ── */}
              {anni.length > 0 && (
                <ChartCard
                  title="Confronto tra anni per linea"
                  subtitle={annoSel.length > 1 ? `confronto diretto: ${annoSel.join(' vs ')}` : 'interventi per anno — barre raggruppate'}
                  icon="Calendar"
                  contextLabel={contextLabel}
                  actions={
                    <ChartToggle value={tipoAnno} onChange={setTipoAnno} options={[
                      { value:'bar',  icon:'BarChart2',  label:'Bar'  },
                      { value:'line', icon:'TrendingUp', label:'Line' },
                    ]} />
                  }
                >
                  <ResponsiveContainer width="100%" height={230}>
                    {tipoAnno === 'line' ? (
                      <LineChart data={datiAnno} margin={{top:5,right:5,left:-18,bottom:0}}>
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="anno" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        {lineeAll.map(l => (
                          <Line key={l} type="monotone" dataKey={l} name={`Linea ${l}`}
                            stroke={catColor(l,lineeAll)}
                            strokeWidth={2.5}
                            dot={{ r:5, fill:catColor(l,lineeAll), strokeWidth:0 }}
                            activeDot={{ r:7, strokeWidth:0 }}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    ) : (
                      <BarChart data={datiAnno} margin={{top:5,right:5,left:-18,bottom:0}} barCategoryGap="30%" barGap={2}>
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="anno" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(128,128,128,0.05)' }} />
                        {lineeAll.map(l => (
                          <Bar key={l} dataKey={l} name={l}
                            fill={catColor(l,lineeAll)}
                            radius={[5,5,0,0]}
                            maxBarSize={35}
                          />
                        ))}
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                  <LegendaPills categorie={lineeAll} allCategorie={lineeAll} dati={datiAnno} />
                </ChartCard>
              )}

              {/* ── HEATMAP LINEA × TIPO INTERVENTO ── */}
              <ChartCard title="Riepilogo Linea × Tipo Intervento" subtitle="" icon="Table" contextLabel={contextLabel}>
                <div className="overflow-x-auto rounded-2xl border border-border/20">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-muted/30">
                        <th className="px-4 py-3 text-left text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 border-b border-border/20 whitespace-nowrap">Linea</th>
                        {tipiAll.map(t => (
                          <th key={t} className="px-4 py-3 text-center text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 border-b border-border/20 whitespace-nowrap">{t}</th>
                        ))}
                        <th className="px-4 py-3 text-center text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 border-b border-border/20">Tot.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineeVis.map((l, li) => {
                        const cnts   = tipiAll.map(t => rowsFiltrate.filter(r => r.linea===l && r.tipointervento===t).length);
                        const maxCnt = Math.max(...cnts, 1);
                        const totL   = rowsFiltrate.filter(r => r.linea===l).length;
                        const col    = catColor(l, lineeAll);
                        return (
                          <tr key={l} className={`border-b border-border/10 last:border-0 ${li%2===0?'':'bg-muted/[0.03]'}`}>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full shrink-0" style={{background:col}} />
                                <span className="text-[10px] font-black uppercase tracking-widest whitespace-nowrap" style={{color:col}}>
                                  {l}
                                </span>
                              </div>
                            </td>
                            {cnts.map((cnt, ci) => {
                              const intensity = cnt / maxCnt;
                              const hex = Math.round(intensity * 55 + 12).toString(16).padStart(2,'0');
                              return (
                                <td key={ci} className="px-4 py-2.5 text-center">
                                  {cnt > 0 ? (
                                    <span className="inline-block px-2.5 py-1 rounded-lg text-[10px] font-black tabular-nums transition-all"
                                      style={{ background:`${col}${hex}`, color:col }}>
                                      {cnt}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-muted-foreground/15 font-bold">—</span>
                                  )}
                                </td>
                              );
                            })}
                            <td className="px-4 py-2.5 text-center">
                              <span className="inline-block px-3 py-1 rounded-lg text-[10px] font-black tabular-nums"
                                style={{ background:`${col}22`, color:col }}>
                                {totL}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-muted/15 border-t-2 border-border/20">
                        <td className="px-4 py-2.5 text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">Totale</td>
                        {tipiAll.map(t => {
                          const cnt = rowsFiltrate.filter(r => r.tipointervento===t).length;
                          return (
                            <td key={t} className="px-4 py-2.5 text-center text-[10px] font-black text-foreground/50 tabular-nums">
                              {cnt||'—'}
                            </td>
                          );
                        })}
                        <td className="px-4 py-2.5 text-center">
                          <span className="text-[12px] font-black text-primary tabular-nums">{totale}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </ChartCard>

            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GraficiRaddrizzatrici;